import type { ApiArchivePreviewItem } from '@Model';
import {
    EnumFilterExpressionOperation,
    EnumMessageLevel,
    EnumOperation,
    type FilterExpression,
    type IApiMessage,
    type ListRequestWithIds,
} from '@synqo/frontend-component-library';
import { formatFileSize } from '@utils/formatFileSize';
import { describeApiMessage } from '@utils/parseApiMessages';
import dayjs, { type Dayjs } from 'dayjs';

export type ExportScope = 'period' | 'selection' | 'view';

export type ExportFormat = 'zip' | 'excel';

export type PeriodPreset = 'lastMonth' | 'lastQuarter' | 'currentYear' | 'custom';

/** A period as the two ISO instants the server filters on. */
export interface Period {
    from: string;
    to: string;
}

/** A period as the two `YYYY-MM-DD` dates the date inputs hold. */
export interface DateInputs {
    from: string;
    to: string;
}

/** Up to this many invoices the archive is waited for and downloaded, above it the notification takes over. */
export const ZIP_IMMEDIATE_LIMIT = 10;

const ZIP_PAGE_SIZE = 500;
const DATE_FIELD = 'date';
const INPUT_FORMAT = 'YYYY-MM-DD';
const NAMED_UNAVAILABLE = 3;

/** The period a preset stands for, relative to `today`. `custom` has none. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function periodOfPreset(preset: PeriodPreset, today: Dayjs = dayjs()): Period | undefined {
    switch (preset) {
        case 'lastMonth': {
            const lastMonth = today.subtract(1, 'month');
            return { from: startOf(lastMonth, 'month'), to: endOf(lastMonth, 'month') };
        }
        case 'lastQuarter': {
            const lastQuarter = today.subtract(3, 'month');
            const quarterStart = lastQuarter.month(lastQuarter.month() - (lastQuarter.month() % 3));
            return { from: startOf(quarterStart, 'month'), to: endOf(quarterStart.add(2, 'month'), 'month') };
        }
        case 'currentYear':
            return { from: startOf(today, 'year'), to: endOf(today, 'day') };
        case 'custom':
            return undefined;
    }
}

/** The two date inputs a preset fills. `custom` fills none. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function inputsOfPreset(preset: PeriodPreset, today: Dayjs = dayjs()): DateInputs | undefined {
    const period = periodOfPreset(preset, today);
    if (period === undefined) {
        return undefined;
    }

    return { from: dayjs(period.from).format(INPUT_FORMAT), to: dayjs(period.to).format(INPUT_FORMAT) };
}

/** The preset `period` matches, or `custom` for a period edited by hand. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function presetOfPeriod(period: Period, today: Dayjs = dayjs()): PeriodPreset {
    const named: PeriodPreset[] = ['lastMonth', 'lastQuarter', 'currentYear'];
    const match = named.find((preset) => {
        const candidate = periodOfPreset(preset, today);
        return candidate?.from === period.from && candidate.to === period.to;
    });

    return match ?? 'custom';
}

/** The whole days from `from` to `to`, or undefined while the inputs are incomplete or reversed. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function periodOfInputs(from: string, to: string): Period | undefined {
    if (!from || !to) {
        return undefined;
    }

    const fromDate = dayjs(from);
    const toDate = dayjs(to);
    if (!fromDate.isValid() || !toDate.isValid() || fromDate.isAfter(toDate)) {
        return undefined;
    }

    return { from: startOf(fromDate, 'day'), to: endOf(toDate, 'day') };
}

export interface SelectionInput {
    scope: ExportScope;
    period: Period | undefined;
    selectedIds: string[];
    /** The ids of the rows the table has loaded, in list order. */
    viewIds: string[];
}

/** The selection the chosen scope sends, or undefined while it has nothing to send yet. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function buildSelection({
    scope,
    period,
    selectedIds,
    viewIds,
}: SelectionInput): ListRequestWithIds | undefined {
    switch (scope) {
        case 'selection':
            return selectedIds.length === 0 ? undefined : selectionOf(selectedIds);
        case 'period':
            return period === undefined ? undefined : periodSelection(period);
        case 'view':
            return viewIds.length === 0 ? undefined : selectionOf(viewIds);
    }
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function selectionOf(ids: string[]): ListRequestWithIds {
    return {
        page_no: 0,
        page_size: ids.length,
        full_text_search: '',
        sort_columns: [],
        include_ids: ids,
        exclude_ids: [],
    };
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function periodSelection(period: Period): ListRequestWithIds {
    return {
        page_no: 0,
        page_size: ZIP_PAGE_SIZE,
        full_text_search: '',
        sort_columns: [],
        filter_columns: periodFilter(period),
        include_ids: [],
        exclude_ids: [],
    };
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function periodFilter(period: Period): FilterExpression {
    return {
        operation: EnumFilterExpressionOperation.And,
        filters: [
            { field: DATE_FIELD, value: period.from, operator: EnumOperation.GreaterEqual },
            { field: DATE_FIELD, value: period.to, operator: EnumOperation.LessEqual },
        ],
    };
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function startOf(date: Dayjs, unit: 'day' | 'month' | 'year'): string {
    return date.startOf(unit).toDate().toISOString();
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function endOf(date: Dayjs, unit: 'day' | 'month' | 'year'): string {
    return date.endOf(unit).toDate().toISOString();
}

export interface SummaryInput {
    format: ExportFormat;
    scope: ExportScope;
    period: Period | undefined;
    /** How many invoices the scope covers, or undefined while nobody counted. */
    documentCount: number | undefined;
    totalFileSize: number;
    /** The printouts the archive would contain, from the archive preview. */
    printouts: ApiArchivePreviewItem[];
    messages: IApiMessage[];
    hasSelection: boolean;
    isLoading: boolean;
}

/** One box in the dialog's summary. */
export interface ExportNotice {
    tone: 'info' | 'warning';
    title: string;
    description?: string;
}

export interface ExportSummary {
    notices: ExportNotice[];
    canStart: boolean;
}

/** What the dialog says about the export it would start, and whether it may start. */
// @ai-generated 2026-09-29T07:37:25+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function summarizeExport({
    format,
    scope,
    period,
    documentCount,
    totalFileSize,
    printouts,
    messages,
    hasSelection,
    isLoading,
}: SummaryInput): ExportSummary {
    if (!hasSelection && scope === 'view') {
        return { notices: [noHitsNotice(scope)], canStart: false };
    }
    if (!hasSelection) {
        const description = 'Wählen Sie einen Zeitraum, markierte Rechnungen oder die aktuelle Ansicht.';
        return { notices: [{ tone: 'info', title: 'Nichts ausgewählt', description }], canStart: false };
    }

    const refusals = messages.filter((message) => message.level >= EnumMessageLevel.Warning);
    if (refusals.length > 0) {
        const notices = refusals.map(
            (message): ExportNotice => ({ tone: 'warning', title: describeApiMessage(message) }),
        );
        return { notices, canStart: false };
    }

    if (isLoading) {
        return { notices: [], canStart: false };
    }

    if (documentCount === 0) {
        return { notices: [noHitsNotice(scope)], canStart: false };
    }
    if (format === 'zip' && documentCount === undefined) {
        const notice: ExportNotice = {
            tone: 'warning',
            title: 'Die Rechnungen konnten nicht gezählt werden',
            description: 'Schließen Sie den Dialog und öffnen Sie ihn erneut.',
        };
        return { notices: [notice], canStart: false };
    }

    const problems = format === 'zip' ? printoutProblems(printouts) : [];
    if (problems.length > 0) {
        return { notices: problems, canStart: false };
    }

    const figures: ExportNotice = {
        tone: 'info',
        title: figuresOf(format, documentCount, totalFileSize),
        description: `${describeScope(scope, period)} · ${describeDelivery(format, documentCount)}`,
    };
    return { notices: [figures], canStart: true };
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function noHitsNotice(scope: ExportScope): ExportNotice {
    if (scope !== 'period') {
        return { tone: 'info', title: 'Keine Rechnungen im gewählten Umfang' };
    }

    return {
        tone: 'info',
        title: 'Keine Rechnungen im gewählten Zeitraum',
        description: 'Passen Sie den Zeitraum an, z. B. über das Preset „Letzter Monat“.',
    };
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function figuresOf(format: ExportFormat, documentCount: number | undefined, totalFileSize: number): string {
    if (documentCount === undefined) {
        return 'Excel-Tabelle (.xlsx)';
    }

    const invoices = describeInvoiceCount(documentCount);
    return format === 'zip' ? `${invoices} · ca. ${formatFileSize(totalFileSize)}` : invoices;
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function describeScope(scope: ExportScope, period: Period | undefined): string {
    switch (scope) {
        case 'period':
            return period === undefined
                ? 'Zeitraum'
                : `Zeitraum ${dayjs(period.from).format('DD.MM.YYYY')} bis ${dayjs(period.to).format('DD.MM.YYYY')}`;
        case 'selection':
            return 'Aktuelle Auswahl';
        case 'view':
            return 'Aktuelle Ansicht';
    }
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function describeDelivery(format: ExportFormat, documentCount: number | undefined): string {
    if (format === 'excel' || isImmediateArchive(documentCount)) {
        return 'wird direkt heruntergeladen';
    }

    return 'wird im Hintergrund erstellt, Sie können weiterarbeiten';
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function printoutProblems(printouts: ApiArchivePreviewItem[]): ExportNotice[] {
    const problems: ExportNotice[] = [];

    const unresolved = printouts.filter((printout) => !printout.resolved);
    if (unresolved.length > 0) {
        const verb = unresolved.length === 1 ? 'ist' : 'sind';
        problems.push({
            tone: 'warning',
            title: `${describeInvoiceCount(unresolved.length)} aus Ihrer Auswahl ${verb} nicht mehr verfügbar`,
            description: 'Laden Sie die Liste neu.',
        });
    }

    const withoutPdf = printouts.filter((printout) => printout.resolved && !printout.available);
    if (withoutPdf.length > 0) {
        const named = withoutPdf.slice(0, NAMED_UNAVAILABLE).map((printout) => printout.label);
        const rest = withoutPdf.length > NAMED_UNAVAILABLE ? ' …' : '';
        problems.push({
            tone: 'warning',
            title: `Für ${describeInvoiceCount(withoutPdf.length)} liegt kein PDF vor: ${named.join(', ')}${rest}`,
            description: 'Ohne PDF ist kein ZIP möglich. Bitte wenden Sie sich an den Support.',
        });
    }

    return problems;
}

/** "1 Rechnung" or "84 Rechnungen". */
// @ai-generated 2026-09-29T07:37:25+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function describeInvoiceCount(count: number): string {
    return count === 1 ? '1 Rechnung' : `${count} Rechnungen`;
}

/** Whether an archive of `invoiceCount` invoices is small enough to wait for. */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function isImmediateArchive(invoiceCount: number | undefined): boolean {
    return invoiceCount !== undefined && invoiceCount <= ZIP_IMMEDIATE_LIMIT;
}
